import "./facilityDetailsEditComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import CommonService from "../../../../../shared/services/common.service";
import {IAPIResponseType} from "../../../../../shared/models/api.model";
import Grid from "@mui/material/Grid";
import FormikInputComponent from "../../../../../shared/components/core/formik-input/FormikInputComponent";
import FormikAutoCompleteDropdown
    from "../../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import {APIConfig} from "../../../../../constants";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import _ from "lodash";
import FormikRadioButtonGroupComponent
    from "../../../../../shared/components/core/formik-radio-button-group/formikRadioButtonGroupComponent";

interface FacilityDetailsEditComponentProps {
    facilityInformation: any
    onUpdateComplete: Function;
    onEditCancel: Function;
}

const facilityAddressValidationSchema = Yup.object({
        addressLine1: Yup.string().nullable()
            .required("Address 1 is required")
            .max(200, 'Address Line 1 cannot exceed 200 characters'),
        addressLine2: Yup.string().nullable().max(200, 'Address Line 2 cannot exceed 200 characters'),
        zipcode: Yup.mixed().required("Zip Code is required"),
        state: Yup.mixed().required("State is required"),
        city: Yup.mixed().required("City is required"),
        newZipCode: Yup.string().when('zipcode', {
            is: (zipcode: any) => (zipcode?.zipcode && zipcode?.zipcode?.toLowerCase() === "other"),
            then: Yup.string().required('Zip Code is required')
                .matches(/^[0-9]{5}$/, "Enter a valid ZIP Code"),
        }),
    }
);

interface facilityInformationAddressFormSchema {
    addressLine1: string,
    addressLine2: string,
    zipcode: any,
    newZipCode: string,
    state: string,
    city: string,
}

const facilityInformationAddressInitialValues: facilityInformationAddressFormSchema = {
    addressLine1: "",
    addressLine2: "",
    zipcode: {},
    newZipCode: "",
    state: "",
    city: "",
}

const facilityInformationValidationSchema = Yup.object({
        name: Yup.string().required("Facility Name is required"),
        facilityType: Yup.mixed().required("Facility Type is required"),
        isInsuranceImageRequired: Yup.boolean().nullable().required("Select Insurance Preferences"),
        addressDetails: facilityAddressValidationSchema
    }
);

interface facilityInformationFormSchema {
    name: string;
    facilityType: string;
    addressDetails: facilityInformationAddressFormSchema,
    isInsuranceImageRequired: null | boolean;
}

const facilityInformationInitialValues: facilityInformationFormSchema = {
    name: "",
    facilityType: "",
    addressDetails: facilityInformationAddressInitialValues,
    isInsuranceImageRequired: null,
}

const FacilityDetailsEditComponent = (props: FacilityDetailsEditComponentProps) => {
    const {facilityInformation, onUpdateComplete, onEditCancel} = props;

    const [facilityBasicInformationInitialValues, setFacilityBasicInformationInitialValues] = useState<facilityInformationFormSchema>(facilityInformationInitialValues);

    const [state, setState] = useState<string>("");
    const [isZipCodeNotFound, setIsZipCodeNotFound] = useState<boolean>(false);

    const handleZipCodeValueChange = useCallback((zipCodeObject: any, setFieldValue: any) => {
        if (zipCodeObject === "") {
            setFieldValue("addressDetails.state", "");
            setFieldValue("addressDetails.city", "");
            setIsZipCodeNotFound(false);
            setState("");
        } else {
            if (zipCodeObject?.zipcode?.toLowerCase() === "other") {
                setIsZipCodeNotFound(true);
                setFieldValue("addressDetails.state", "");
                setFieldValue("addressDetails.city", "");
            } else {
                setIsZipCodeNotFound(false);
                setFieldValue("addressDetails.state", zipCodeObject.state);
                setFieldValue("addressDetails.city", zipCodeObject.city);
            }
        }
        setFieldValue("newZipCode", "");
    }, []);

    const onFormSubmit = useCallback((values: any, {
        setSubmitting,
        setErrors,
    }: FormikHelpers<any>) => {
        const payload = _.cloneDeep({...values});
        payload.type = payload.facilityType.code
        if (payload.addressDetails["newZipCode"]) {
            payload.addressDetails["zipcode"] = payload.addressDetails["newZipCode"];
            payload.addressDetails["state"] = payload.addressDetails?.state?.state;
            payload.addressDetails["city"] = payload.addressDetails?.city?.city;
            delete payload.addressDetails["newZipCode"];
        } else {
            payload.addressDetails["zipcode"] = payload.addressDetails?.zipcode?.zipcode;
        }
        setSubmitting(true);
        CommonService._facility.editFacilityDetails(facilityInformation?.id, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                onUpdateComplete(response.data);
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            });

    }, [facilityInformation, onUpdateComplete]);


    useEffect(() => {
        if (facilityInformation) {
            const facilityInformationObj = {...facilityInformation};
            const facilityInformationDetails: any = {addressDetails: {}};
            facilityInformationDetails['facilityType'] = facilityInformationObj?.type;
            facilityInformationDetails['isInsuranceImageRequired'] = facilityInformationObj?.isInsuranceImageRequired;
            facilityInformationDetails['name'] = facilityInformationObj?.name;
            facilityInformationDetails.addressDetails.addressLine1 = facilityInformationObj?.addressDetails?.addressLine1;
            facilityInformationDetails.addressDetails.addressLine2 = facilityInformationObj?.addressDetails?.addressLine2;

            if (!facilityInformationObj.addressDetails.zipcodeDetails) {
                facilityInformationDetails.addressDetails.newZipCode = facilityInformationObj?.addressDetails?.zipcode;
                facilityInformationDetails.addressDetails.zipcode = {
                    zipcode: "Other"
                };
                facilityInformationDetails.addressDetails.state = facilityInformationObj?.addressDetails?.stateDetails;
                facilityInformationDetails.addressDetails.city = facilityInformationObj?.addressDetails?.cityDetails;
                setIsZipCodeNotFound(true);
            } else {
                facilityInformationDetails.addressDetails.zipcode = facilityInformationObj?.addressDetails?.zipcodeDetails;
                facilityInformationDetails.addressDetails.state = facilityInformationObj?.addressDetails?.stateDetails?.state;
                facilityInformationDetails.addressDetails.city = facilityInformationObj?.addressDetails?.cityDetails?.city;
                setIsZipCodeNotFound(false);
            }
            setState(facilityInformationObj.addressDetails?.state);

            setFacilityBasicInformationInitialValues(facilityInformationDetails);
        }
    }, [facilityInformation]);

    return (
        <div className={'facility-details-edit-component details-tab-data-content'}>
            <Formik
                validationSchema={facilityInformationValidationSchema}
                initialValues={facilityBasicInformationInitialValues}
                validateOnChange={false}
                validateOnBlur={true}
                enableReinitialize={true}
                validateOnMount={true}
                onSubmit={onFormSubmit}
            >
                {({isSubmitting, dirty, values, isValid, setFieldValue, validateForm, errors}) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values, errors]);
                    return (
                        <Form noValidate={true} className={"patient-personal-information-holder"}>
                            <div className={'t-form-controls-wrapper'}>
                                <div className="form-sub-title">Basic Details</div>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'name'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Facility Name'}
                                                        placeholder={'Enter Facility Name'}
                                                        type={"text"}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'facilityType'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Facility Type'}
                                                        placeholder={'Enter Facility Type'}
                                                        type={"text"}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>

                                <div className={'horizontal-line'}></div>
                                <div className="form-sub-title">Address Details</div>
                                <Field name={'addressDetails.addressLine1'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikInputComponent
                                                label={'Address Line 1'}
                                                placeholder={'Address Line 1'}
                                                type={"text"}
                                                required={true}
                                                formikField={field}
                                            />
                                        )
                                    }
                                </Field>
                                <Grid container spacing={2}>

                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'addressDetails.addressLine2'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Address Line 2'}
                                                        placeholder={'Address Line 2'}
                                                        type={"text"}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'addressDetails.zipcode'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'ZIP Code'}
                                                        placeholder={'Start typing for ZIP Code'}
                                                        required={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                        openOnFocus={true}
                                                        url={APIConfig.ZIPCODE_LIST.URL}
                                                        method={APIConfig.ZIPCODE_LIST.METHOD}
                                                        searchMode={'serverSide'}
                                                        data={[]}
                                                        valueKey={'zipcode'}
                                                        dataListKey={"data.docs"}
                                                        onUpdate={(value: any) => {
                                                            handleZipCodeValueChange(value, setFieldValue)
                                                        }}
                                                        //Hiding other option from zipcode
                                                        // defaultData={[
                                                        //     {
                                                        //         zipcode: "Other"
                                                        //     }
                                                        // ]}
                                                        getOptionLabel={(option => (option.zipcode) || '')}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                {isZipCodeNotFound && <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'addressDetails.newZipCode'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            placeholder={"Enter New ZIP Code"}
                                                            label={'New ZIP Code'}
                                                            required={true}
                                                            maxLength={5}
                                                            type={"text"}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>

                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'addressDetails.state'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            label={'State'}
                                                            required={true}
                                                            formikField={field}
                                                            url={APIConfig.STATE_LIST.URL}
                                                            method={APIConfig.STATE_LIST.METHOD}
                                                            searchMode={'serverSide'}
                                                            openOnFocus={true}
                                                            dataListKey={"data.docs"}
                                                            data={[]}
                                                            valueKey={'state'}
                                                            onUpdate={(value: any) => {
                                                                setState(value?.state);
                                                                field.form.setFieldValue("city", '');
                                                            }}
                                                            getOptionLabel={(option => (option.state) || '')}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'addressDetails.city'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            label={'City'}
                                                            placeholder={'City'}
                                                            required={true}
                                                            id="city_auto_complete"
                                                            formikField={field}
                                                            searchMode={'serverSide'}
                                                            url={APIConfig.CITY_LIST.URL}
                                                            method={APIConfig.CITY_LIST.METHOD}
                                                            valueKey={'city'}
                                                            disabled={!state}
                                                            payload={{
                                                                state: state,
                                                                page: 1,
                                                                limit: 100
                                                            }}
                                                            data={[]}
                                                            getOptionLabel={(option => (option.city) || '')}
                                                            dataListKey={"data.docs"}
                                                        />
                                                    )
                                                }
                                            </Field>

                                        </Grid>
                                        <Grid item xs={12} sm={6}>

                                        </Grid>
                                    </Grid></>
                                }
                                {!isZipCodeNotFound && values?.addressDetails?.zipcode?.zipcode && values?.addressDetails?.zipcode?.zipcode !== "Other" &&
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'addressDetails.state'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'State'}
                                                            placeholder={'Enter state'}
                                                            required={true}
                                                            readOnly={true}
                                                            formikField={field}
                                                            type={"text"}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'addressDetails.city'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'City'}
                                                            placeholder={'Enter city'}
                                                            required={true}
                                                            readOnly={true}
                                                            formikField={field}
                                                            type={"text"}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                }
                                <section>
                                    <div className={'horizontal-line'}/>
                                    <div className="form-sub-title">Insurance Preference</div>
                                    <div className={'d-flex align-items-center'}>
                                        <div className='physician-address-same-as-facility-header'>
                                            Is uploading insurance images mandatory for this facility? *
                                        </div>
                                        <div className={'mrg-left-20'}>
                                            <Field name={'isInsuranceImageRequired'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikRadioButtonGroupComponent
                                                            direction={"row"}
                                                            formikField={field}
                                                            titleKey={'title'}
                                                            valueKey={'code'}
                                                            isValueBoolean={true}
                                                            onUpdate={(value: any) => {
                                                            }}
                                                            options={CommonService._staticData.yesNoOptions}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                </section>
                            </div>
                            <div className={"form-action-section-edit"}>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"secondary"}
                                    type={"button"}
                                    variant={"outlined"}
                                    disabled={isSubmitting}
                                    handleClick={() => {
                                        onEditCancel();
                                    }}
                                    fullWidth={false}
                                >
                                    Cancel
                                </ButtonComponent>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"primary"}
                                    type={"submit"}
                                    variant={"contained"}
                                    disabled={!(isValid && dirty)}
                                    fullWidth={false}
                                    isLoading={isSubmitting}
                                >
                                    Save
                                </ButtonComponent>
                            </div>
                        </Form>
                    )
                }
                }
            </Formik>
        </div>
    )
        ;

};

export default FacilityDetailsEditComponent;
